import { useState, useEffect, useRef } from 'react';
import api from "../../../services/api";
import { useTranslation } from 'react-i18next';
import { EcgHighcharts } from '../Ecg/EcgHighcharts'
import { dateOnlyFormatter } from '../../../utils/dateFormatter'
import { Form } from 'react-bootstrap';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import Loading from '../Loading';
import styles from './OneIntervalChart.module.scss';

export function OneIntervalChart({ 
  displayName, 
  branchType,
  availableRecords = [],    
  apiUrl,
  containerClassName 
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [chartCache, setChartCache] = useState({});
  const chartRef = useRef(null);

  const yAxisSettings = [{ title: { text: '' }, labels: { format: '{value} mV' }, opposite: false }];

  const [selectedRecordUid, setSelectedRecordUid] = useState(null);
  
  // Initialize selectedRecordUid when availableRecords changes
  useEffect(() => {
    if (availableRecords && availableRecords.length > 0 && !selectedRecordUid) {
      setSelectedRecordUid(availableRecords[0].recordUid);
    }
  }, [availableRecords]);

  // Find current record index
  const currentRecordIndex = availableRecords.findIndex(r => r.recordUid === selectedRecordUid);

  const canGoToPrevious = currentRecordIndex < availableRecords.length - 1;
  const canGoToNext = currentRecordIndex > 0;

  useEffect(() => {
    if (selectedRecordUid && apiUrl) {
      // Check cache first
      if (chartCache[selectedRecordUid]) {
        setChartData(chartCache[selectedRecordUid]);
      } else {
        setLoading(true);
        api
          .get(`${apiUrl}/${selectedRecordUid}/chart`)
          .then((response) => {
            const data = response.data;
            // Cache the data first
            setChartCache(prev => ({
              ...prev,
              [selectedRecordUid]: data
            }));
            // Then update chart data
            setChartData(data);
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      }
    }
  }, [selectedRecordUid, apiUrl]);

  useEffect(() => {
    if (chartData && chartData.data && chartRef.current) {
      // Only setup Y axis on first load
      if (!chartRef.current.chart) {
        chartRef.current.setupYAxis(yAxisSettings);
      }
      // Update data without recreating the chart
      chartRef.current.setInitialData(chartData.data, null, chartData.markers);      
    }
  }, [chartData]);

  const handlePreviousRecord = () => {
    if (canGoToPrevious) {
      setSelectedRecordUid(availableRecords[currentRecordIndex + 1].recordUid);
    }
  };

  const handleNextRecord = () => {
    if (canGoToNext) {
      setSelectedRecordUid(availableRecords[currentRecordIndex - 1].recordUid);
    }
  };

  const specificChartOptions = {
    chart: {
      height: undefined,
      width: undefined
    },
    title: {
      text: ''
    },
    legend: {
      enabled: false
    },
    xAxis: {
      softMin: 0,
      softMax: 1440,
      max: 1440,
      minorTicks: true
    },
    tooltip: {
      valueDecimals: 2,
      valueSuffix: ' mV',
      split: true,
      shared: false,
      xDateFormat: '%M:%S.%L'
    },
  }; 


  return (
    <>
      <div className={styles.headerContainer}>
        <h3>{displayName}</h3>
        <span>25 mm/s</span>
      </div>
      <div className={styles.chartContainer}>
        {loading && (
          <div className={styles.loadingOverlay}>
            <Loading />
          </div>
        )}
        <EcgHighcharts 
          ref={chartRef} 
          isStockChart={false} 
          isHideControls={true} 
          specificChartOptions={specificChartOptions}
          hideCalibrationSeries={false}
          calibrationSeriesType={'small'}
          containerClassName={containerClassName}
          style={{ visibility: loading && !chartData ? 'hidden' : 'visible' }}
        />    
          <div className={styles.textUnderChart}>            
            {availableRecords.length > 0 && (
                <div className={styles.dateControls}>
                  <button
                    className={styles.arrowButton}
                    onClick={handleNextRecord}
                    disabled={!canGoToNext}
                    title={t('ecg12.nextDate')}
                  >
                    <ChevronLeft size={16} />
                  </button>
                
                <Form.Select 
                  size="sm" 
                  value={selectedRecordUid || ''} 
                    onChange={(e) => setSelectedRecordUid(e.target.value)}
                  className={styles.dateSelect}
                >
                  {(() => {
                    // Group records by date for display
                    const recordsByDate = {};
                    availableRecords.forEach(record => {
                      const dateStr = dateOnlyFormatter(record.recordDate);
                      if (!recordsByDate[dateStr]) {
                        recordsByDate[dateStr] = [];
                      }
                      recordsByDate[dateStr].push(record);
                    });
                    
                    // Create options with proper numbering for multiple records per date
                    return availableRecords.map((record, index) => {
                      const dateStr = dateOnlyFormatter(record.recordDate);
                      const recordsForDate = recordsByDate[dateStr];
                      let displayText = dateStr;
                      
                      if (recordsForDate.length > 1) {
                        const recordIndex = recordsForDate.findIndex(r => r.recordUid === record.recordUid);
                        displayText = `${dateStr} (${recordIndex + 1})`;
                      }
                      
                      return (
                        <option key={record.recordUid} value={record.recordUid}>
                          {displayText}
                        </option>
                      );
                    });
                  })()}
                </Form.Select>
                
                  <button
                    className={styles.arrowButton}
                    onClick={handlePreviousRecord}
                    disabled={!canGoToPrevious}
                    title={t('ecg12.previousDate')}
                  >
                    <ChevronRight size={16} />
                  </button>

              </div>
            )}
          </div>
      </div>
    </>
  );
}
